import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { useCreateFeeScheduleQuery } from 'Hooks/api/JOONAdmin/useCreateFeeScheduleQuery'
import { useUpdateFeeScheduleQuery } from 'Hooks/api/JOONAdmin/useUpdateFeeScheduleQuery'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { ChargeFeeType, FeeSchedule } from 'Utils/types/employerInfo'
import { useJoonAdminContext } from 'Contexts/JOONAdmin/JOONAdminContext'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { displayDate } from 'Utils/helpers/dateUtils'

export type FeeScheduleFormValues = {
  startsOn: Date
  processingFee: number
  baseProcessingFee: number | null
  chargeFeePer: ChargeFeeType
  alternativeReimbursement: boolean
}

// Build a local "YYYY-MM-01" so the selected month is not shifted by UTC conversion.
const toMonthStartString = (date: Date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-01`
}

const firstOfNextMonth = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 1)
}

const parseCurrency = (value: number | null): number | null => {
  const trimmed = String(value ?? '').trim()
  if (trimmed === '') return null

  const parsed = Number(trimmed)
  return Number.isNaN(parsed) ? null : parsed
}

const reimbursementChangeNote = (toPayroll: boolean, startsOn: Date) => {
  const month = displayDate(startsOn, false)
  return toPayroll
    ? `From ${month}, employees are reimbursed through payroll instead of by JOON.`
    : `From ${month}, JOON reimburses employees directly. They'll need to connect a deposit account before they can add purchases or get paid.`
}

export const useFeeScheduleForm = (
  closeModal: () => void,
  countryCode: CountryCode,
  feeSchedule?: FeeSchedule | null
) => {
  const isEdit = !!feeSchedule

  const {
    state: { employerInfo }
  } = useEmployerInfoContext()
  const currentAlternativeReimbursement =
    employerInfo?.countryConfigs[countryCode]?.alternativeReimbursement ?? false

  const defaultValues: Partial<FeeScheduleFormValues> = feeSchedule
    ? {
        startsOn: new Date(`${feeSchedule.startsOn}T00:00:00`),
        processingFee: feeSchedule.processingFee,
        baseProcessingFee: feeSchedule.baseProcessingFee,
        chargeFeePer: feeSchedule.chargeFeePer,
        alternativeReimbursement: feeSchedule.alternativeReimbursement
      }
    : {
        startsOn: firstOfNextMonth(),
        processingFee: 0,
        baseProcessingFee: null,
        chargeFeePer: ChargeFeeType.Invite,
        alternativeReimbursement: currentAlternativeReimbursement
      }

  const { control, handleSubmit } = useForm<FeeScheduleFormValues>({ defaultValues })

  const {
    state: { selectedEmployer }
  } = useJoonAdminContext()

  const { mutateAsync: createFeeScheduleQuery, isLoading: isCreating } = useCreateFeeScheduleQuery()
  const { mutateAsync: updateFeeScheduleQuery, isLoading: isUpdating } = useUpdateFeeScheduleQuery()
  const { showModal } = useConfirmationModalContext()

  const handleSubmission: SubmitHandler<FeeScheduleFormValues> = async (data) => {
    if (!selectedEmployer?.id) return

    const question = `Are you sure you want to ${isEdit ? 'update' : 'add'} this fee schedule?`
    const switchesReimbursement =
      !isEdit && data.alternativeReimbursement !== currentAlternativeReimbursement
    const confirmed = await showModal(
      `${isEdit ? 'Update' : 'Add'} fee schedule: ${CountryNames[countryCode]}`,
      switchesReimbursement
        ? `${reimbursementChangeNote(data.alternativeReimbursement, data.startsOn)} ${question}`
        : question
    )
    if (!confirmed) return

    let response
    if (feeSchedule) {
      response = await updateFeeScheduleQuery({
        employerId: selectedEmployer.id,
        feeScheduleId: feeSchedule.id,
        processingFee: Number(data.processingFee),
        baseProcessingFee: parseCurrency(data.baseProcessingFee),
        chargeFeePer: data.chargeFeePer
      })
    } else {
      response = await createFeeScheduleQuery({
        employerId: selectedEmployer.id,
        countryCode,
        startsOn: toMonthStartString(data.startsOn),
        processingFee: Number(data.processingFee),
        baseProcessingFee: parseCurrency(data.baseProcessingFee),
        chargeFeePer: data.chargeFeePer,
        alternativeReimbursement: data.alternativeReimbursement
      })
    }

    if (response.success) closeModal()
  }

  const handleError: SubmitErrorHandler<FeeScheduleFormValues> = (errors) => {
    toast.error(errors.processingFee?.message ?? 'Please check the fee schedule details.')
  }

  return {
    control,
    handleSubmit,
    handleSubmission,
    handleError,
    isEdit,
    isLoading: isCreating || isUpdating
  }
}
